import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Mail, Send, MessageCircle, Twitter, User, Instagram, RefreshCw, Phone, UserCircle, Copy, Check, ChevronDown, ChevronUp, List, BarChart2, Search } from 'lucide-react';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import Timeline from './Timeline';
import EventDetailView from '../../conversion-events/components/EventDetailView';
import TransactionDetailView from '../../conversion-events/components/TransactionDetailView';

/**
 * Shared cache for user details across all UserDetailExpanded instances
 * Cache key: `${teamId}_${websiteId}_${userId}`
 * This ensures that if a user expands the same row multiple times, data is fetched only once
 */
const userDetailsCache = new Map();

/** Backend expects numeric website_id OR UUID site_id — never pass UUID as website_id (parseInt corrupts it). */
function websiteScopeParams(websiteId) {
  if (websiteId == null || websiteId === '') return {};
  const s = String(websiteId).trim();
  if (/^\d+$/.test(s)) return { website_id: parseInt(s, 10) };
  return { site_id: s };
}

/**
 * UserDetailExpanded Component
 * Inline expandable section showing user details with tabs (User Details, Timeline, DApps, Tokens)
 * Fetches data from /api/user-identities/:user_id endpoint ON DEMAND when component mounts (when row is expanded)
 * Uses shared cache to avoid refetching already loaded user details
 */
const UserDetailExpanded = ({ user, teamId, websiteId }) => {
  const navigate = useNavigate();
  const params = useParams();
  const websiteScope = useMemo(() => websiteScopeParams(websiteId), [websiteId]);
  const activeTab = 'userDetails'; // Single-page layout (no tabs) - kept for compatibility
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [dapps, setDapps] = useState([]);
  const [dappsPagination, setDappsPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [tokens, setTokens] = useState([]);
  const [tokensPagination, setTokensPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDapps, setIsLoadingDapps] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [error, setError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshing, setRefreshing] = useState({ networth: false, dapps: false, tokens: false, allTransactions: false });
  // Right container timeline (events + transactions for this user)
  const [rightTimelineData, setRightTimelineData] = useState([]);
  const [rightTimelinePagination, setRightTimelinePagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loadingRightTimeline, setLoadingRightTimeline] = useState(false);
  const [loadingMoreRightTimeline, setLoadingMoreRightTimeline] = useState(false);
  // Chart uses all data (high limit), independent of Load More - fetched when chart view is active
  const [chartTimelineData, setChartTimelineData] = useState([]);
  const [loadingChartData, setLoadingChartData] = useState(false);
  // Bottom right container (40-60 layout): DApps, Tokens, All Transactions
  const [bottomRightTab, setBottomRightTab] = useState('dapps');
  const [allTransactionsData, setAllTransactionsData] = useState([]);
  const [allTransactionsPagination, setAllTransactionsPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loadingAllTransactions, setLoadingAllTransactions] = useState(false);
  const [loadingMoreAllTransactions, setLoadingMoreAllTransactions] = useState(false);
  const [loadingMoreDapps, setLoadingMoreDapps] = useState(false);
  const [loadingMoreTokens, setLoadingMoreTokens] = useState(false);
  const [refreshMenuOpen, setRefreshMenuOpen] = useState(false);
  const refreshMenuRef = useRef(null);
  const [activityFeedViewMode, setActivityFeedViewMode] = useState('chart'); // 'chart' = stacked line, 'timeline' = list
  const [activityFeedChartRange, setActivityFeedChartRange] = useState('30D'); // 24H, 7D, 30D, 3M, 12M
  const [userPropertiesSearch, setUserPropertiesSearch] = useState('');
  const [expandedRightItem, setExpandedRightItem] = useState(null); // { key, type }
  const [expandedEventDetails, setExpandedEventDetails] = useState(null); // full event when expanded
  const abortControllerRef = useRef(null);
  const timelineAbortControllerRef = useRef(null);
  const tabContentRef = useRef(null);
  const windowScrollYRef = useRef(null);
  const isPaginationUpdatingRef = useRef(false);
  
  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    accentcolor: "#0f172a",
    borderColor: "#DDE2EE"
  };

  // Fetch user details from API ON DEMAND with caching when component mounts (only when row is expanded)
  useEffect(() => {
    // Support both distinctId and userId for backward compatibility
    const userIdentifier = user?.distinctId || user?.userId;
    if (!userIdentifier || !teamId || !websiteId) {
      return;
    }

    // Create cache key using the identifier
    const cacheKey = `${teamId}_${websiteId}_${userIdentifier}`;

    // Check shared cache first
    const cachedData = userDetailsCache.get(cacheKey);
    if (cachedData) {
      setUserDetails(cachedData.userDetails);
      setDapps(cachedData.dapps || []);
      setTokens(cachedData.tokens || []);
      setIsLoading(false);
      return;
    }

    // Abort previous request if component is unmounting or user changed
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const fetchUserDetails = async () => {
      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsLoading(true);
      setError(null);

      try {
        // Always use distinct_id (prefer distinctId, fallback to userId for backward compatibility)
        const userIdentifier = user.distinctId || user.userId;
        
        const response = await axiosInstance.get(`/user-identities/${userIdentifier}`, {
          params: {
            team_id: teamId,
            ...websiteScope,
            lookup_type: 'distinct_id'
          },
          signal: abortController.signal
        });

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        if (response.data && response.data.success) {
          const fetchedUserDetails = response.data.userDetails;
          const fetchedDapps = response.data.dapps || [];
          const fetchedTokens = response.data.tokens || [];
          
          // Store in shared cache
          userDetailsCache.set(cacheKey, {
            userDetails: fetchedUserDetails,
            dapps: fetchedDapps,
            tokens: fetchedTokens
          });
          
          setUserDetails(fetchedUserDetails);
          setDapps(fetchedDapps);
          setTokens(fetchedTokens);
        } else {
          setError('Failed to load user details');
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        console.error('Error fetching user details:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load user details');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchUserDetails();

    // Cleanup: abort request if component unmounts or dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [user?.distinctId, user?.userId, teamId, websiteId, websiteScope]); // Re-fetch if distinctId, userId, teamId, or websiteId changes

  // Fetch right container timeline (events + transactions only) when user details are loaded
  const rightTimelineAbortRef = useRef(null);
  useEffect(() => {
    const userIdentifier = user?.distinctId || user?.userId;
    if (!userIdentifier || !teamId || !websiteId || !userDetails) return;

    const fetchRightTimeline = async (page = 1, append = false) => {
      const isLoadMore = append && page > 1;
      if (isLoadMore) setLoadingMoreRightTimeline(true);
      else setLoadingRightTimeline(true);

      if (rightTimelineAbortRef.current) rightTimelineAbortRef.current.abort();
      const ac = new AbortController();
      rightTimelineAbortRef.current = ac;

      try {
        const response = await axiosInstance.get(`/user-identities/${userIdentifier}/timeline`, {
          params: {
            team_id: teamId,
            ...websiteScope,
            page,
            limit: 25,
            lookup_type: 'distinct_id',
            types_only: 'customEvent,transaction'
          },
          signal: ac.signal
        });
        if (ac.signal.aborted) return;
        if (response.data?.success) {
          const data = response.data.data || [];
          const pagination = response.data.pagination || {};
          if (append) {
            setRightTimelineData(prev => [...prev, ...data]);
          } else {
            setRightTimelineData(data);
          }
          setRightTimelinePagination(pagination);
        }
      } catch (err) {
        if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
        console.error('Error fetching right timeline:', err);
      } finally {
        if (!ac.signal.aborted) {
          setLoadingRightTimeline(false);
          setLoadingMoreRightTimeline(false);
        }
      }
    };

    fetchRightTimeline(1, false);

    return () => {
      if (rightTimelineAbortRef.current) rightTimelineAbortRef.current.abort();
    };
  }, [user?.distinctId, user?.userId, teamId, websiteId, websiteScope, userDetails]);

  // Fetch all timeline data for chart (limit 100) when chart view is active - independent of Load More
  const chartAbortRef = useRef(null);
  useEffect(() => {
    const userIdentifier = user?.distinctId || user?.userId;
    if (!userIdentifier || !teamId || !websiteId || !userDetails || activityFeedViewMode !== 'chart') return;

    if (chartAbortRef.current) chartAbortRef.current.abort();
    const ac = new AbortController();
    chartAbortRef.current = ac;
    setLoadingChartData(true);

    axiosInstance.get(`/user-identities/${userIdentifier}/timeline`, {
      params: {
        team_id: teamId,
        ...websiteScope,
        page: 1,
        limit: 100,
        lookup_type: 'distinct_id',
        types_only: 'customEvent,transaction'
      },
      signal: ac.signal
    })
      .then(res => {
        if (res.data?.success && !ac.signal.aborted) {
          setChartTimelineData(res.data.data || []);
        }
      })
      .catch(err => {
        if (err?.name !== 'AbortError' && err?.name !== 'CanceledError') console.error('Error fetching chart data:', err);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoadingChartData(false);
      });

    return () => { chartAbortRef.current?.abort(); };
  }, [user?.distinctId, user?.userId, teamId, websiteId, websiteScope, userDetails, activityFeedViewMode]);

  // Fetch full event details when event row is expanded
  useEffect(() => {
    if (!expandedRightItem || expandedRightItem.type !== 'customEvent' || !expandedRightItem.eventId) return;
    let cancelled = false;
    const fetchEvent = async () => {
      try {
        const res = await axiosInstance.get(`/events/${expandedRightItem.eventId}`, {
          params: { team_id: teamId }
        });
        if (!cancelled && res.data?.success && res.data?.event) {
          setExpandedEventDetails(res.data.event);
        }
      } catch (e) {
        if (!cancelled) console.error('Error fetching event details:', e);
      }
    };
    fetchEvent();
    return () => { cancelled = true; };
  }, [expandedRightItem?.eventId, expandedRightItem?.type, teamId]);

  // Clear expanded event when collapsing
  useEffect(() => {
    if (!expandedRightItem || expandedRightItem.type !== 'customEvent') {
      setExpandedEventDetails(null);
    }
  }, [expandedRightItem]);

  const userIdentifier = user?.distinctId || user?.userId;

  // Fetch DApps data when bottom right dapps tab is active (Load More pagination)
  useEffect(() => {
    if (bottomRightTab !== 'dapps' || !userIdentifier || !teamId || !websiteId) {
      return;
    }

    const fetchDapps = async () => {
      setIsLoadingDapps(true);
      setError(null);

      try {
        const response = await axiosInstance.get(`/user-identities/${userIdentifier}/dapps`, {
          params: {
            team_id: teamId,
            ...websiteScope,
            lookup_type: 'distinct_id',
            page: 1,
            limit: 25
          }
        });

        if (response.data && response.data.success) {
          setDapps(response.data.data || []);
          setDappsPagination(response.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
        } else {
          setDapps([]);
        }
      } catch (err) {
        console.error('Error fetching dapps data:', err);
        setDapps([]);
      } finally {
        setIsLoadingDapps(false);
      }
    };

    fetchDapps();
  }, [bottomRightTab, userIdentifier, teamId, websiteId, websiteScope]);

  // Fetch Tokens data when bottom right tokens tab is active (Load More pagination)
  useEffect(() => {
    if (bottomRightTab !== 'tokens' || !userIdentifier || !teamId || !websiteId) {
      return;
    }

    const fetchTokens = async () => {
      setIsLoadingTokens(true);
      setError(null);

      try {
        const response = await axiosInstance.get(`/user-identities/${userIdentifier}/tokens`, {
          params: {
            team_id: teamId,
            ...websiteScope,
            lookup_type: 'distinct_id',
            page: 1,
            limit: 25
          }
        });

        if (response.data && response.data.success) {
          setTokens(response.data.data || []);
          setTokensPagination(response.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
        } else {
          setTokens([]);
        }
      } catch (err) {
        console.error('Error fetching tokens data:', err);
        setTokens([]);
      } finally {
        setIsLoadingTokens(false);
      }
    };

    fetchTokens();
  }, [bottomRightTab, userIdentifier, teamId, websiteId, websiteScope]);

  // Fetch All Transactions when bottom right allTransactions tab is active (Load More pagination)
  useEffect(() => {
    if (bottomRightTab !== 'allTransactions' || !userIdentifier || !teamId || !websiteId) {
      return;
    }

    const fetchAllTransactions = async () => {
      setLoadingAllTransactions(true);

      try {
        const response = await axiosInstance.get(`/user-identities/${userIdentifier}/timeline`, {
          params: {
            team_id: teamId,
            ...websiteScope,
            page: 1,
            limit: 25,
            lookup_type: 'distinct_id',
            types_only: 'allTransaction'
          }
        });

        if (response.data?.success) {
          const data = response.data.data || [];
          setAllTransactionsData(data);
          setAllTransactionsPagination(response.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
        } else {
          setAllTransactionsData([]);
        }
      } catch (err) {
        console.error('Error fetching all transactions:', err);
        setAllTransactionsData([]);
      } finally {
        setLoadingAllTransactions(false);
      }
    };

    fetchAllTransactions();
  }, [bottomRightTab, userIdentifier, teamId, websiteId, websiteScope]);

  // Format date for display (dd-mm-yyyy)
  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return format(date, 'dd-MM-yyyy');
    } catch (e) {
      return 'N/A';
    }
  };

  // Format wallet address
  const formatWallet = (address) => {
    if (!address) return '';
    if (address.length > 12) {
      return `${address.substring(0, 6)}...${address.substring(address.length - 6)}`;
    }
    return address;
  };

  // Format date/time for timeline
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy HH:mm:ss');
    } catch (e) {
      return 'N/A';
    }
  };

  // Format date only for timeline headers
  const formatDateHeader = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy');
    } catch (e) {
      return 'N/A';
    }
  };

  // Format time only for timeline rows
  const formatTimeOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return format(date, 'HH:mm:ss');
    } catch (e) {
      return 'N/A';
    }
  };

  // Format duration (seconds to readable format)
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (seconds >= 3600) {
      return `${hours}h ${minutes}m`;
    } else if (seconds >= 60) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Format total time spent for user details
  const formatTimeSpentForDetails = (seconds) => {
    if (seconds == null || seconds === 0) return '0s';
    const s = typeof seconds === 'number' ? seconds : parseFloat(seconds);
    if (isNaN(s)) return '0s';
    return formatDuration(s);
  };

  // Pagination component
  const Pagination = ({ pagination, onPageChange, isLoading }) => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const handlePageChange = (newPage, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        // Remove focus from button to prevent browser from scrolling to it
        e.currentTarget.blur();
      }
      
      // Save current window scroll position
      const savedScrollY = window.scrollY;
      windowScrollYRef.current = savedScrollY;
      
      // Mark that we're updating pagination
      isPaginationUpdatingRef.current = true;
      
      // Immediately scroll tab content to top
      if (tabContentRef.current) {
        tabContentRef.current.scrollTop = 0;
      }
      
      // Lock window scroll position immediately
      window.scrollTo({ top: savedScrollY, behavior: 'auto' });
      
      // Call onPageChange
      onPageChange(newPage);
      
      // Keep scroll locked until data loads and renders
      // The useEffect hooks will clear this flag after data loads
    };

    return (
      <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: `1px solid ${styles.borderColor}` }}>
        <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => handlePageChange(pagination.page - 1, e)}
            disabled={pagination.page === 1 || isLoading}
            className="px-3 py-1 text-sm border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              borderColor: styles.borderColor,
              color: pagination.page === 1 ? styles.textSecondary : styles.textPrimary,
              fontFamily: 'Raleway, sans-serif'
            }}
            type="button"
          >
            Previous
          </button>
          <span className="text-sm px-3" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={(e) => handlePageChange(pagination.page + 1, e)}
            disabled={pagination.page >= pagination.totalPages || isLoading}
            className="px-3 py-1 text-sm border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              borderColor: styles.borderColor,
              color: pagination.page >= pagination.totalPages ? styles.textSecondary : styles.textPrimary,
              fontFamily: 'Raleway, sans-serif'
            }}
            type="button"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, addressKey) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAddress(addressKey);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Close refresh menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (refreshMenuRef.current && !refreshMenuRef.current.contains(event.target)) {
        setRefreshMenuOpen(false);
      }
    };
    if (refreshMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [refreshMenuOpen]);

  // Prevent unwanted scrolling during pagination updates
  useEffect(() => {
    const handleScroll = (e) => {
      if (isPaginationUpdatingRef.current && windowScrollYRef.current !== null) {
        // Prevent scroll if we're in a pagination update
        window.scrollTo({ top: windowScrollYRef.current, behavior: 'auto' });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: false });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Refresh functions for each data type
  const refreshNetworth = async () => {
    console.log('🔄 [Refresh Networth] Starting refresh...', {
      userId: user?.userId,
      teamId,
      websiteId
    });
    
    const userIdentifier = user?.distinctId || user?.userId;
    if (!userIdentifier || !teamId || !websiteId) {
      console.warn('⚠️ [Refresh Networth] Missing required parameters');
      return;
    }
    
    setRefreshing(prev => ({ ...prev, networth: true }));
    setRefreshError(null); // Clear previous refresh errors
    
    try {
      console.log('📡 [Refresh Networth] Triggering backend refresh...', {
        endpoint: `/user-identities/refresh-wallet-data`,
        body: { user_id: userIdentifier, team_id: teamId, ...websiteScope, fields: ['networth'] }
      });
      // Trigger backend refresh (always use distinct_id - everything is by distinct_id)
      const refreshResponse = await axiosInstance.post(`/user-identities/refresh-wallet-data`, {
        user_id: userIdentifier,
        team_id: teamId,
        ...websiteScope,
        lookup_type: 'distinct_id',
        fields: ['networth']
      });

      console.log('✅ [Refresh Networth] Backend refresh initiated:', refreshResponse.data);

      // Wait a bit for the refresh to complete, then fetch updated data
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Fetch updated data (always use distinct_id)
      const lookupType = 'distinct_id';
      const response = await axiosInstance.get(`/user-identities/${userIdentifier}`, {
        params: {
          team_id: teamId,
          ...websiteScope,
          lookup_type: lookupType
        }
      });

      if (response.data && response.data.success && response.data.userDetails) {
        const oldNetworth = userDetails?.walletNetworth;
        const newNetworth = response.data.userDetails.walletNetworth;
        
        console.log('📊 [Refresh Networth] Updating state...', {
          oldNetworth,
          newNetworth,
          changed: oldNetworth !== newNetworth
        });
        
        setUserDetails(prev => {
          if (!prev) {
            return response.data.userDetails;
          }
          return {
            ...prev,
            walletNetworth: response.data.userDetails.walletNetworth
          };
        });
        
        // Update cache
        const cacheKey = `${teamId}_${websiteId}_${userIdentifier}`;
        const cachedData = userDetailsCache.get(cacheKey);
        if (cachedData) {
          cachedData.userDetails.walletNetworth = response.data.userDetails.walletNetworth;
          userDetailsCache.set(cacheKey, cachedData);
        }
        
        console.log('✅ [Refresh Networth] Refresh completed successfully');
      }
    } catch (err) {
      console.error('❌ [Refresh Networth] Error occurred:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      // Handle credit errors specifically - use refreshError instead of error
      if (err.response?.status === 402 && err.response?.data?.error === 'INSUFFICIENT_CREDITS') {
        const errorData = err.response.data;
        setRefreshError(
          `Insufficient credits. Required: ${errorData.availability?.requiredCredits || 20} credits, ` +
          `Available: ${errorData.availability?.availableCredits || 0} credits. Please purchase more credits to continue.`
        );
      } else {
        setRefreshError(err.response?.data?.message || err.message || 'Failed to refresh networth');
      }
    } finally {
      setRefreshing(prev => ({ ...prev, networth: false }));
    }
  };

  const refreshDapps = async () => {
    console.log('🔄 [Refresh DApps] Starting refresh...', {
      userId: user?.userId,
      teamId,
      websiteId,
      currentDappsCount: dapps.length
    });
    
    const userIdentifier = user?.distinctId || user?.userId;
    if (!userIdentifier || !teamId || !websiteId) {
      console.warn('⚠️ [Refresh DApps] Missing required parameters');
      return;
    }
    
    setRefreshing(prev => ({ ...prev, dapps: true }));
    setRefreshError(null); // Clear previous refresh errors
    
    try {
      console.log('📡 [Refresh DApps] Triggering backend refresh...', {
        endpoint: `/user-identities/refresh-wallet-data`,
        body: { user_id: userIdentifier, team_id: teamId, ...websiteScope, fields: ['dapps'] }
      });
      
      // Trigger backend refresh (always use distinct_id - everything is by distinct_id)
      const refreshResponse = await axiosInstance.post(`/user-identities/refresh-wallet-data`, {
        user_id: userIdentifier,
        team_id: teamId,
        ...websiteScope,
        lookup_type: 'distinct_id',
        fields: ['dapps']
      });

      console.log('✅ [Refresh DApps] Backend refresh initiated:', refreshResponse.data);

      // Wait a bit for the refresh to complete, then fetch updated data
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Fetch updated data (always use distinct_id)
      const lookupType = 'distinct_id';
      const response = await axiosInstance.get(`/user-identities/${userIdentifier}`, {
        params: {
          team_id: teamId,
          ...websiteScope,
          lookup_type: lookupType
        }
      });

      console.log('✅ [Refresh DApps] API response received:', {
        success: response.data?.success,
        dappsCount: response.data?.dapps?.length || 0,
        dapps: response.data?.dapps
      });

      if (response.data && response.data.success) {
        const fetchedDapps = response.data.dapps || [];
        const oldCount = dapps.length;
        const newCount = fetchedDapps.length;
        
        console.log('📊 [Refresh DApps] Updating state...', {
          oldCount,
          newCount,
          changed: oldCount !== newCount || JSON.stringify(dapps) !== JSON.stringify(fetchedDapps)
        });
        
        setDapps(fetchedDapps);
        
        // Update cache
        const cacheKey = `${teamId}_${websiteId}_${userIdentifier}`;
        const cachedData = userDetailsCache.get(cacheKey);
        if (cachedData) {
          cachedData.dapps = fetchedDapps;
          userDetailsCache.set(cacheKey, cachedData);
          console.log('💾 [Refresh DApps] Cache updated');
        } else {
          console.log('⚠️ [Refresh DApps] No cache entry found to update');
        }
        
        console.log('✅ [Refresh DApps] Refresh completed successfully', {
          finalDappsCount: fetchedDapps.length
        });
      } else {
        console.warn('⚠️ [Refresh DApps] Invalid response structure:', response.data);
      }
    } catch (err) {
      console.error('❌ [Refresh DApps] Error occurred:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      // Handle credit errors specifically - use refreshError instead of error
      if (err.response?.status === 402 && err.response?.data?.error === 'INSUFFICIENT_CREDITS') {
        const errorData = err.response.data;
        setRefreshError(
          `Insufficient credits. Required: ${errorData.availability?.requiredCredits || 8} credits, ` +
          `Available: ${errorData.availability?.availableCredits || 0} credits. Please purchase more credits to continue.`
        );
      } else {
        setRefreshError(err.response?.data?.message || err.message || 'Failed to refresh dapps');
      }
    } finally {
      setRefreshing(prev => ({ ...prev, dapps: false }));
      console.log('🏁 [Refresh DApps] Refresh process finished');
    }
  };

  const refreshTokens = async () => {
    console.log('🔄 [Refresh Tokens] Starting refresh...', {
      userId: user?.userId,
      teamId,
      websiteId,
      currentTokensCount: tokens.length
    });
    
    const userIdentifier = user?.distinctId || user?.userId;
    if (!userIdentifier || !teamId || !websiteId) {
      console.warn('⚠️ [Refresh Tokens] Missing required parameters');
      return;
    }
    
    setRefreshing(prev => ({ ...prev, tokens: true }));
    setRefreshError(null); // Clear previous refresh errors
    
    try {
      console.log('📡 [Refresh Tokens] Triggering backend refresh...', {
        endpoint: `/user-identities/refresh-wallet-data`,
        body: { user_id: userIdentifier, team_id: teamId, ...websiteScope, fields: ['tokens'] }
      });
      
      // Trigger backend refresh (always use distinct_id - everything is by distinct_id)
      const refreshResponse = await axiosInstance.post(`/user-identities/refresh-wallet-data`, {
        user_id: userIdentifier,
        team_id: teamId,
        ...websiteScope,
        lookup_type: 'distinct_id',
        fields: ['tokens']
      });

      console.log('✅ [Refresh Tokens] Backend refresh initiated:', refreshResponse.data);

      // Wait a bit for the refresh to complete, then fetch updated data
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Fetch updated data (always use distinct_id)
      const lookupType = 'distinct_id';
      const response = await axiosInstance.get(`/user-identities/${userIdentifier}`, {
        params: {
          team_id: teamId,
          ...websiteScope,
          lookup_type: lookupType
        }
      });

      if (response.data && response.data.success) {
        const fetchedTokens = response.data.tokens || [];
        setTokens(fetchedTokens);
        
        // Update cache
        const cacheKey = `${teamId}_${websiteId}_${userIdentifier}`;
        const cachedData = userDetailsCache.get(cacheKey);
        if (cachedData) {
          cachedData.tokens = fetchedTokens;
          userDetailsCache.set(cacheKey, cachedData);
        }
        
        console.log('✅ [Refresh Tokens] Refresh completed successfully');
      }
    } catch (err) {
      console.error('❌ [Refresh Tokens] Error occurred:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      // Handle credit errors specifically - use refreshError instead of error
      if (err.response?.status === 402 && err.response?.data?.error === 'INSUFFICIENT_CREDITS') {
        const errorData = err.response.data;
        setRefreshError(
          `Insufficient credits. Required: ${errorData.availability?.requiredCredits || 12} credits, ` +
          `Available: ${errorData.availability?.availableCredits || 0} credits. Please purchase more credits to continue.`
        );
      } else {
        setRefreshError(err.response?.data?.message || err.message || 'Failed to refresh tokens');
      }
    } finally {
      setRefreshing(prev => ({ ...prev, tokens: false }));
    }
  };

  const refreshAllTransactions = async () => {
    const userIdentifier = user?.distinctId || user?.userId;
    if (!userIdentifier || !teamId || !websiteId) return;
    setRefreshError(null);
    setRefreshing(prev => ({ ...prev, allTransactions: true }));
    try {
      await axiosInstance.post('/user-identities/refresh-wallet-data', {
        user_id: userIdentifier,
        team_id: teamId,
        ...websiteScope,
        fields: ['transactions']
      });
      const response = await axiosInstance.get(`/user-identities/${userIdentifier}/timeline`, {
        params: { team_id: teamId, ...websiteScope, page: 1, limit: 25, lookup_type: 'distinct_id', types_only: 'allTransaction' }
      });
      if (response.data?.success) {
        setAllTransactionsData(response.data.data || []);
        setAllTransactionsPagination(response.data.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
      }
    } catch (err) {
      setRefreshError(err.response?.data?.message || err.message || 'Failed to refresh all transactions');
    } finally {
      setRefreshing(prev => ({ ...prev, allTransactions: false }));
    }
  };

  // Copy button component
  const CopyButton = ({ address, addressKey }) => {
    if (!address) return null;
    const isCopied = copiedAddress === addressKey;
    return (
      <button
        onClick={() => copyToClipboard(address, addressKey)}
        className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors"
        title="Copy address"
      >
        {isCopied ? (
          <Check className="h-3 w-3" style={{ color: '#22c55e' }} />
        ) : (
          <Copy className="h-3 w-3" style={{ color: styles.textSecondary }} />
        )}
      </button>
    );
  };

  return (
    <div style={{ backgroundColor: styles.primaryBg }}>
      {refreshError && (
        <div
          className="mx-6 mt-4 mb-0 p-4 rounded border flex items-center justify-between"
          style={{
            backgroundColor: '#fef2f2',
            borderColor: '#ef4444',
            color: '#ef4444',
            fontFamily: 'Raleway, sans-serif'
          }}
        >
          <span>Error: {refreshError}</span>
          <button
            onClick={() => setRefreshError(null)}
            className="ml-4 text-sm hover:underline"
            style={{ color: '#ef4444' }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div ref={tabContentRef} className="overflow-y-auto p-6 scrollbar-subtle">
        {(
          <>
            {isLoading ? (
              <div className="py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                Loading user details...
              </div>
            ) : error && !userDetails ? (
              // Only show error replacing content if userDetails is null (initial load error)
              <div className="py-8 text-center" style={{ color: '#ef4444', fontFamily: 'Raleway, sans-serif' }}>
                Error: {error}
              </div>
            ) : userDetails ? (
            <div className="grid gap-6 w-full" style={{ gridTemplateColumns: '40fr 60fr', gridTemplateRows: 'auto auto' }}>
              {/* Row 1 - Left: User details | Right: Timeline */}
              {/* Left container - 40% width, all user details stacked downwards */}
              <div 
                className="flex flex-col overflow-y-auto min-w-0 max-h-[400px] rounded-2xl p-4 scrollbar-subtle"
                style={{ border: '1px solid #e5e7eb' }}
              >
                <div className="flex flex-col flex-shrink-0 -mx-4 px-4 pb-3 mb-3" style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                  <span className="text-sm font-bold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                    {userDetails.distinctId ?? userDetails.userId ?? 'N/A'}
                  </span>
                </div>
                <div className="flex flex-col">
                <DetailRow label="First Visit" value={formatDateDDMMYYYY(userDetails.firstVisit)} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="Last Visit" value={formatDateDDMMYYYY(userDetails.lastVisit)} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="Total Sessions" value={userDetails.totalSessions || 0} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="Total Page Views" value={userDetails.totalPageViews || 0} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="Total Time Spent" value={formatTimeSpentForDetails(userDetails.totalTimeSpentSeconds)} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRowWithDropdown
                  label="Referrer"
                  options={[
                    { label: 'First', value: userDetails.firstReferrer || 'N/A' },
                    { label: 'Last', value: userDetails.lastReferrer || 'N/A' }
                  ]}
                  styles={styles}
                  dividerColor="#e5e7eb"
                  labelColor="#0f172a"
                  valueColor="#475569"
                />
                <DetailRowWithDropdown
                  label="Location"
                  options={[
                    { label: 'First', value: userDetails.firstLocation || 'N/A' },
                    { label: 'Last', value: userDetails.lastLocation || 'N/A' }
                  ]}
                  styles={styles}
                  dividerColor="#e5e7eb"
                  labelColor="#0f172a"
                  valueColor="#475569"
                />
                <DetailRow 
                  label="Bounce Status" 
                  value={userDetails.bounceStatus || 'N/A'} 
                  highlight={userDetails.bounceStatus === 'Engaged'}
                  styles={styles}
                  dividerColor="#e5e7eb"
                  labelColor="#0f172a"
                  valueColor="#475569"
                />
                <DetailRow label="Is Web3 User" value={userDetails.isWeb3User || 'No'} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="Device Type" value={userDetails.deviceType || 'N/A'} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="Browser" value={userDetails.browser || 'N/A'} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="OS" value={userDetails.os || 'N/A'} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow 
                  label="Primary Wallet" 
                  value={formatWallet(userDetails.primaryWallet)} 
                  fullValue={userDetails.primaryWallet}
                  addressKey="primaryWallet"
                  copyButton={userDetails.primaryWallet ? <CopyButton address={userDetails.primaryWallet} addressKey="primaryWallet" /> : null}
                  styles={styles}
                  dividerColor="#e5e7eb"
                  labelColor="#0f172a"
                  valueColor="#475569"
                />
                <OtherWalletsRow
                  wallets={userDetails.otherWallets}
                  formatWallet={formatWallet}
                  styles={styles}
                  dividerColor="#e5e7eb"
                  labelColor="#0f172a"
                  valueColor="#475569"
                  CopyButton={CopyButton}
                />
                <DetailRow label="Wallet Type" value={userDetails.walletType || 'N/A'} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow label="Chain" value={userDetails.chain || 'N/A'} styles={styles} dividerColor="#e5e7eb" labelColor="#0f172a" valueColor="#475569" />
                <DetailRow 
                  label="Wallet Networth" 
                  value={userDetails.walletNetworth ? `$${userDetails.walletNetworth.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}` : 'N/A'}
                  highlight={!!userDetails.walletNetworth}
                  styles={styles}
                  dividerColor="#e5e7eb"
                  labelColor="#0f172a"
                  valueColor="#475569"
                />
                <DetailRow 
                  label="Updated at" 
                  value={userDetails.updatedAt ? formatDateTime(userDetails.updatedAt) : 'N/A'}
                  styles={styles}
                  dividerColor="#e5e7eb"
                  labelColor="#0f172a"
                  valueColor="#475569"
                />
                </div>
              </div>
              {/* Right container - 60% width: Activity feed (chart / timeline) */}
              <div 
                className="min-w-0 flex flex-col rounded-2xl p-4 scrollbar-subtle"
                style={{ border: '1px solid #e5e7eb', maxHeight: '400px' }}
              >
                {/* Activity feed header: title | time selector | view switch */}
                <div className="flex flex-col flex-shrink-0 -mx-4 px-4 pb-3 mb-3" style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-bold flex-shrink-0" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Activity feed</span>
                    <div className="flex items-center gap-2">
                      {activityFeedViewMode === 'chart' && (
                        <div className="flex items-center">
                          {['24H', '7D', '30D', '3M', '12M'].map((r, index) => (
                            <button
                              key={r}
                              type="button"
                              onClick={() => setActivityFeedChartRange(r)}
                              className={`px-2 py-1 text-xs font-medium transition-colors ${
                                index === 0 ? 'rounded-l-md' : ''
                              } ${index === 4 ? 'rounded-r-md' : ''} ${
                                activityFeedChartRange === r
                                  ? 'bg-gray-100 text-[#0f172a] border border-gray-300 z-10'
                                  : 'bg-white text-[#475569] hover:bg-gray-50 border border-gray-300'
                              } ${index > 0 ? '-ml-px' : ''}`}
                              style={{ fontFamily: 'Raleway, sans-serif' }}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setActivityFeedViewMode(prev => prev === 'chart' ? 'timeline' : 'chart')}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded flex-shrink-0"
                        style={{ fontFamily: 'Raleway, sans-serif', color: styles.textPrimary, border: `1px solid ${styles.borderColor}`, backgroundColor: styles.primaryBg }}
                        title={activityFeedViewMode === 'chart' ? 'Switch to timeline' : 'Switch to chart'}
                      >
                        {activityFeedViewMode === 'chart' ? (
                          <><List className="h-3 w-3" /> Timeline</>
                        ) : (
                          <><BarChart2 className="h-3 w-3" /> Chart</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
                  {loadingRightTimeline ? (
                    <div className="py-8 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>Loading timeline...</div>
                  ) : rightTimelineData.length === 0 ? (
                    <div className="py-8 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>No events or transactions</div>
                  ) : activityFeedViewMode === 'chart' ? (
                    /* Stacked line chart: uses chartTimelineData (all data, limit 100), independent of Load More. Range from current time. */
                    (() => {
                      const data = chartTimelineData.length > 0 ? chartTimelineData : rightTimelineData;
                      const rangeMs = { '24H': 24 * 60 * 60 * 1000, '7D': 7 * 24 * 60 * 60 * 1000, '30D': 30 * 24 * 60 * 60 * 1000, '3M': 90 * 24 * 60 * 60 * 1000, '12M': 365 * 24 * 60 * 60 * 1000 };
                      const hasEvents = data.some(i => i.type === 'customEvent');
                      const hasTransactions = data.some(i => i.type === 'transaction' || i.type === 'allTransaction');
                      const showLegend = hasEvents && hasTransactions;
                      const endMs = Date.now();
                      const startMs = endMs - (rangeMs[activityFeedChartRange] || rangeMs['7D']);
                      const bucketMap = {};
                      if (activityFeedChartRange === '24H') {
                        for (let t = startMs; t <= endMs; t += 30 * 60 * 1000) {
                          const k = String(t);
                          bucketMap[k] = { bucket: k, events: 0, transactions: 0, sortKey: t };
                        }
                      } else if (activityFeedChartRange === '12M') {
                        const d = new Date(startMs);
                        const endD = new Date(endMs);
                        while (d <= endD) {
                          const k = format(d, 'yyyy-MM');
                          bucketMap[k] = { bucket: k, events: 0, transactions: 0, sortKey: new Date(d.getFullYear(), d.getMonth(), 1).getTime() };
                          d.setMonth(d.getMonth() + 1);
                        }
                      } else {
                        const bucketSizeMs = activityFeedChartRange === '3M' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
                        for (let t = startMs; t <= endMs; t += bucketSizeMs) {
                          const dt = new Date(t);
                          if (activityFeedChartRange === '3M') {
                            dt.setDate(dt.getDate() - dt.getDay());
                            dt.setHours(0, 0, 0, 0);
                          } else {
                            dt.setHours(0, 0, 0, 0);
                          }
                          const k = activityFeedChartRange === '3M' ? format(dt, 'yyyy-MM-dd') : format(dt, 'yyyy-MM-dd');
                          const sortKey = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
                          if (!bucketMap[k]) bucketMap[k] = { bucket: k, events: 0, transactions: 0, sortKey };
                        }
                      }
                      data.forEach((item) => {
                        const d = item.dateTime ? new Date(item.dateTime) : null;
                        if (!d || d.getTime() < startMs || d.getTime() > endMs) return;
                        let bucketKey, sortKey;
                        if (activityFeedChartRange === '24H') {
                          const bucketMs = Math.floor(d.getTime() / (30 * 60 * 1000)) * (30 * 60 * 1000);
                          bucketKey = String(bucketMs);
                          sortKey = bucketMs;
                        } else if (activityFeedChartRange === '3M') {
                          const weekStart = new Date(d);
                          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                          weekStart.setHours(0, 0, 0, 0);
                          bucketKey = format(weekStart, 'yyyy-MM-dd');
                          sortKey = weekStart.getTime();
                        } else if (activityFeedChartRange === '12M') {
                          bucketKey = format(d, 'yyyy-MM');
                          sortKey = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
                        } else {
                          bucketKey = format(d, 'yyyy-MM-dd');
                          sortKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
                        }
                        let b = bucketMap[bucketKey];
                        if (!b) {
                          b = { bucket: bucketKey, events: 0, transactions: 0, sortKey };
                          bucketMap[bucketKey] = b;
                        }
                        const isEvent = item.type === 'customEvent';
                        if (isEvent) b.events += 1;
                        else b.transactions += 1;
                      });
                      const chartData = Object.values(bucketMap)
                        .sort((a, b) => a.sortKey - b.sortKey)
                        .map(b => ({
                          ...b,
                          label: activityFeedChartRange === '24H'
                            ? format(new Date(parseInt(b.bucket, 10)), 'HH:mm')
                            : activityFeedChartRange === '12M'
                              ? format(new Date(b.bucket + '-01'), 'MMM yyyy')
                              : format(new Date(b.bucket), 'MMM d')
                        }));
                      const CustomTooltip = ({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white p-3 border shadow-lg rounded text-sm" style={{ borderColor: styles.borderColor, fontFamily: 'Raleway, sans-serif' }}>
                              <p className="font-medium mb-1" style={{ color: styles.textPrimary }}>{label}</p>
                              {payload.map((entry, i) => (
                                <p key={i} style={{ color: entry.color }}>{entry.name}: {entry.value}</p>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      };
                      const CustomLegend = ({ payload }) => {
                        if (!showLegend || !payload || payload.length === 0) return null;
                        return (
                          <div className="flex justify-center gap-4 mt-2" style={{ fontFamily: 'Raleway, sans-serif', fontSize: 11 }}>
                            {payload.map((entry, i) => (
                              <div key={i} className="flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
                                <span style={{ color: styles.textSecondary }}>{entry.value}</span>
                              </div>
                            ))}
                          </div>
                        );
                      };
                      return (
                        <div className="flex flex-col flex-1 min-h-[280px]">
                          <div className="flex-1 min-h-[220px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={styles.borderColor} />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: 'Raleway, sans-serif', fill: styles.textSecondary }} />
                                <YAxis domain={[0, 'auto']} tick={{ fontSize: 10, fontFamily: 'Raleway, sans-serif', fill: styles.textSecondary }} allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend content={<CustomLegend />} />
                                {hasEvents && <Line type="monotone" dataKey="events" name="Events" stroke="#8979FF" strokeWidth={2} dot={false} strokeLinecap="round" strokeLinejoin="round" baseValue={0} />}
                                {hasTransactions && <Line type="monotone" dataKey="transactions" name="Transactions" stroke="#FFC9A4" strokeWidth={2} dot={false} strokeLinecap="round" strokeLinejoin="round" baseValue={0} />}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <>
                      {(() => {
                        const groupedByDate = {};
                        rightTimelineData.forEach((item, idx) => {
                          const dateKey = item.dateTime ? format(new Date(item.dateTime), 'yyyy-MM-dd') : 'no-date';
                          if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
                          groupedByDate[dateKey].push({ item, idx });
                        });
                        const dates = Object.keys(groupedByDate).sort();

                        return (
                          <div className="flex-1 min-h-0">
                            {dates.map((dateKey) => {
                              const entries = groupedByDate[dateKey];
                              const dateLabel = dateKey === 'no-date' ? 'N/A' : formatDateHeader(entries[0]?.item?.dateTime || dateKey);
                              return (
                                <div key={dateKey} className="mb-4">
                                  <div className="text-sm font-semibold py-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif', borderBottom: '1px solid #e5e7eb' }}>
                                    {dateLabel}
                                  </div>
                                  <div>
                                    {entries.map(({ item, idx }) => {
                                      const isEvent = item.type === 'customEvent';
                                      const isTransaction = item.type === 'transaction' || item.type === 'allTransaction';
                                      const key = isEvent ? `evt-${item.eventId}-${idx}` : `tx-${item.txHash || idx}-${idx}`;
                                      const isExpanded = expandedRightItem?.key === key;
                                      const displayTime = formatTimeOnly(item.dateTime);
                                      const nameLabel = isEvent ? (item.eventName || 'Event') : (
                                        (item.contractName || item.contract_name || item.summary || 'Transaction') +
                                        ((item.functionName || item.function_name) ? ` · ${item.functionName || item.function_name}` : '')
                                      );

                                      return (
                                        <div key={key} className="flex">
                                          <div className="py-2 flex-shrink-0" style={{ width: '70px' }}>
                                            <span className="text-xs whitespace-nowrap" style={{ color: '#475569', fontFamily: 'Raleway, sans-serif' }}>
                                              {displayTime}
                                            </span>
                                          </div>
                                          <div className="flex-1 min-w-0" style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <button
                                              type="button"
                                              onClick={() => setExpandedRightItem(isExpanded ? null : { key, type: item.type, eventId: item.eventId })}
                                              className="w-full text-left py-2 flex items-center justify-between gap-2 hover:bg-gray-50 transition-colors"
                                              style={{ backgroundColor: isExpanded ? '#f8fafc' : 'transparent' }}
                                            >
                                              <span className="text-sm font-medium truncate" style={{ color: '#0f172a', fontFamily: 'Raleway, sans-serif' }}>
                                                {nameLabel}
                                              </span>
                                              {isExpanded ? <ChevronUp className="h-4 w-4 flex-shrink-0" style={{ color: '#475569' }} /> : <ChevronDown className="h-4 w-4 flex-shrink-0" style={{ color: '#475569' }} />}
                                            </button>
                                            {isExpanded && (
                                              <div className="pb-4 pt-1">
                                                {isEvent ? (
                                                  expandedEventDetails ? (
                                                    <EventDetailView event={expandedEventDetails} />
                                                  ) : (
                                                    <div className="py-4 text-sm" style={{ color: styles.textSecondary }}>Loading event details...</div>
                                                  )
                                                ) : (
                                                  <TransactionDetailView
                                                    transaction={{
                                                      tx_hash: item.txHash,
                                                      txHash: item.txHash,
                                                      contract_name: item.contractName || item.contract_name,
                                                      contractName: item.contractName || item.contract_name,
                                                      function_name: item.functionName || item.function_name,
                                                      functionName: item.functionName || item.function_name,
                                                      transaction_time: item.dateTime,
                                                      transactionTime: item.dateTime,
                                                      value: item.value,
                                                      chain: item.chain,
                                                      summary: item.summary,
                                                      ...item
                                                    }}
                                                  />
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                            {rightTimelineData.length > 0 && rightTimelinePagination.totalPages > 1 && rightTimelinePagination.page < rightTimelinePagination.totalPages && !loadingRightTimeline && (
                              <div className="mt-4 pt-4 flex justify-center flex-shrink-0" style={{ borderTop: '1px solid #e5e7eb' }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const nextPage = (rightTimelinePagination.page || 1) + 1;
                                    const userIdentifier = user?.distinctId || user?.userId;
                                    if (!userIdentifier || !teamId || !websiteId) return;
                                    setLoadingMoreRightTimeline(true);
                                    axiosInstance.get(`/user-identities/${userIdentifier}/timeline`, {
                                      params: {
                                        team_id: teamId,
                                        ...websiteScope,
                                        page: nextPage,
                                        limit: 25,
                                        lookup_type: 'distinct_id',
                                        types_only: 'customEvent,transaction'
                                      }
                                    }).then(res => {
                                      if (res.data?.success) {
                                        const data = res.data.data || [];
                                        setRightTimelineData(prev => [...prev, ...data]);
                                        setRightTimelinePagination(res.data.pagination || rightTimelinePagination);
                                      }
                                    }).catch(console.error).finally(() => setLoadingMoreRightTimeline(false));
                                  }}
                                  disabled={loadingMoreRightTimeline}
                                  className="px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50"
                                  style={{ backgroundColor: '#f1f5f9', color: '#0f172a', fontFamily: 'Raleway, sans-serif', border: '1px solid #e5e7eb' }}
                                >
                                  {loadingMoreRightTimeline ? 'Loading...' : 'Load More'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>

              {/* Row 2 - Left: User Properties | Right: DApps/Tokens/All Transactions */}
              <div 
                className="flex flex-col overflow-y-auto min-w-0 max-h-[400px] rounded-2xl p-4 scrollbar-subtle"
                style={{ border: '1px solid #e5e7eb' }}
              >
                <div className="flex items-center justify-between gap-3 flex-shrink-0 -mx-4 px-4 pb-3 mb-3" style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                  <span className="text-sm font-bold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>User Properties</span>
                  <div className="relative w-[180px]">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: styles.textSecondary }} />
                    <input
                      type="text"
                      placeholder="Search properties..."
                      value={userPropertiesSearch}
                      onChange={(e) => setUserPropertiesSearch(e.target.value)}
                      className="w-full pl-8 pr-2 py-1 text-xs rounded-md border"
                      style={{ fontFamily: 'Raleway, sans-serif', borderColor: styles.borderColor, color: styles.textPrimary, backgroundColor: styles.primaryBg }}
                    />
                  </div>
                </div>
                {!userDetails?.custom_properties || Object.keys(userDetails.custom_properties).filter(k => !k.startsWith('$')).length === 0 ? (
                  <div className="py-8 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                    No custom properties for this user
                  </div>
                ) : (() => {
                  const filtered = Object.entries(userDetails.custom_properties)
                    .filter(([key]) => !key.startsWith('$'))
                    .filter(([key, value]) => {
                      const q = (userPropertiesSearch || '').trim().toLowerCase();
                      if (!q) return true;
                      const label = key.replace(/_/g, ' ').toLowerCase();
                      const valStr = typeof value === 'object' ? JSON.stringify(value) : String(value).toLowerCase();
                      return label.includes(q) || valStr.includes(q);
                    });
                  if (filtered.length === 0) {
                    return (
                      <div className="py-8 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                        No properties match your search
                      </div>
                    );
                  }
                  return filtered.map(([key, value]) => {
                      const label = key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                      const dividerStyle = { borderBottom: '1px solid #e5e7eb' };
                      if (Array.isArray(value)) {
                        return (
                          <div key={key} className="flex flex-col py-2 px-4">
                            <div className="flex flex-col pb-2" style={dividerStyle}>
                            <div className="text-sm" style={{ color: '#0f172a', fontFamily: 'Raleway, sans-serif' }}>{label}:</div>
                            <div className="flex flex-wrap gap-2 mt-1.5">
                              {value.map((item, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center px-2 py-1 rounded text-sm"
                                  style={{ backgroundColor: '#f1f5f9', color: '#475569', fontFamily: 'Raleway, sans-serif', border: '1px solid #e5e7eb' }}
                                >
                                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                                </span>
                              ))}
                            </div>
                            </div>
                          </div>
                        );
                      }
                      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                        return (
                          <div key={key} className="py-2 px-4">
                            <div className="pb-2" style={dividerStyle}>
                            <div className="text-sm" style={{ color: '#0f172a', fontFamily: 'Raleway, sans-serif' }}>{label}:</div>
                            <div className="pl-4 mt-1 space-y-1">
                              {Object.entries(value).map(([k, v]) => (
                                <div key={k} className="flex items-start justify-between py-1 gap-2 text-sm" style={{ fontFamily: 'Raleway, sans-serif' }}>
                                  <span style={{ color: '#0f172a' }}>{k.replace(/_/g, ' ')}:</span>
                                  <span className="font-medium text-right" style={{ color: '#475569' }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                </div>
                              ))}
                            </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <DetailRow
                          key={key}
                          label={label}
                          value={String(value)}
                          styles={styles}
                          dividerColor="#e5e7eb"
                          labelColor="#0f172a"
                          valueColor="#475569"
                        />
                      );
                    });
                })()}
              </div>
              {/* Right: DApps | Tokens | All Transactions tabs with Load More */}
              <div 
                className="min-w-0 flex flex-col rounded-2xl overflow-hidden scrollbar-subtle p-4"
                style={{ border: '1px solid #e5e7eb', maxHeight: '400px' }}
              >
                {/* Sub-tabs with Refresh buttons - same header design as other containers */}
                <div className="flex items-baseline justify-between flex-shrink-0 -mx-4 px-4 pb-3 mb-3" style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                  <div className="flex flex-1 items-baseline">
                    {['dapps', 'tokens', 'allTransactions'].map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setBottomRightTab(tab)}
                        className="flex-1 py-2 px-3 text-sm font-bold"
                        style={{
                          fontFamily: 'Raleway, sans-serif',
                          borderBottom: bottomRightTab === tab ? `2px solid ${styles.textPrimary}` : '2px solid transparent',
                          color: bottomRightTab === tab ? styles.textPrimary : styles.textSecondary,
                          marginBottom: '-1px'
                        }}
                      >
                        {tab === 'dapps' ? 'DApps' : tab === 'tokens' ? 'Tokens' : 'All Transactions'}
                      </button>
                    ))}
                  </div>
                  <div className="flex-shrink-0 relative" ref={refreshMenuRef}>
                    <button
                      type="button"
                      onClick={() => setRefreshMenuOpen(!refreshMenuOpen)}
                      disabled={refreshing.networth || refreshing.dapps || refreshing.tokens}
                      className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded disabled:opacity-50"
                      style={{ fontFamily: 'Raleway, sans-serif', color: styles.textPrimary, border: `1px solid ${styles.borderColor}`, backgroundColor: styles.primaryBg }}
                    >
                      <RefreshCw className={`h-3 w-3 ${(refreshing.networth || refreshing.dapps || refreshing.tokens) ? 'animate-spin' : ''}`} />
                      Refresh
                      <ChevronDown className={`h-3 w-3 transition-transform ${refreshMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {refreshMenuOpen && (
                      <div
                        className="absolute right-0 top-full mt-1 z-50 min-w-[160px] py-1 rounded-lg shadow-lg"
                        style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}
                      >
                        <button
                          type="button"
                          onClick={() => { refreshNetworth(); setRefreshMenuOpen(false); }}
                          disabled={refreshing.networth}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ fontFamily: 'Raleway, sans-serif', color: styles.textPrimary }}
                        >
                          <RefreshCw className={`h-3 w-3 ${refreshing.networth ? 'animate-spin' : ''}`} />
                          Net Worth
                        </button>
                        <button
                          type="button"
                          onClick={() => { refreshDapps(); setRefreshMenuOpen(false); }}
                          disabled={refreshing.dapps}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ fontFamily: 'Raleway, sans-serif', color: styles.textPrimary }}
                        >
                          <RefreshCw className={`h-3 w-3 ${refreshing.dapps ? 'animate-spin' : ''}`} />
                          DApps
                        </button>
                        <button
                          type="button"
                          onClick={() => { refreshTokens(); setRefreshMenuOpen(false); }}
                          disabled={refreshing.tokens}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ fontFamily: 'Raleway, sans-serif', color: styles.textPrimary }}
                        >
                          <RefreshCw className={`h-3 w-3 ${refreshing.tokens ? 'animate-spin' : ''}`} />
                          Tokens
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0">
                  {bottomRightTab === 'dapps' && (
                    <>
                      {isLoadingDapps ? (
                        <div className="py-6 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>Loading DApps...</div>
                      ) : dapps.length === 0 ? (
                        <div className="py-6 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>No DApps found for this user</div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>DApp Name</th>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Address</th>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Chain</th>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Count</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dapps.map((dapp, index) => (
                                  <tr key={index} style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                                    <td className="py-2 px-3 text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{dapp.name || 'N/A'}</td>
                                    <td className="py-2 px-3 font-mono text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                                      <div className="flex items-center gap-2">
                                        <span>{dapp.address ? formatWallet(dapp.address) : 'N/A'}</span>
                                        {dapp.address && <CopyButton address={dapp.address} addressKey={`dapp-br-${index}`} />}
                                      </div>
                                    </td>
                                    <td className="py-2 px-3 text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{dapp.chain || 'N/A'}</td>
                                    <td className="py-2 px-3 text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{dapp.count || 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {dapps.length > 0 && dappsPagination.totalPages > 1 && dappsPagination.page < dappsPagination.totalPages && !loadingMoreDapps && (
                            <div className="mt-4 pt-4 flex justify-center flex-shrink-0" style={{ borderTop: '1px solid #e5e7eb' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  const nextPage = (dappsPagination.page || 1) + 1;
                                  const userIdentifier = user?.distinctId || user?.userId;
                                  if (!userIdentifier || !teamId || !websiteId) return;
                                  setLoadingMoreDapps(true);
                                  axiosInstance.get(`/user-identities/${userIdentifier}/dapps`, {
                                    params: { team_id: teamId, ...websiteScope, lookup_type: 'distinct_id', page: nextPage, limit: 25 }
                                  }).then(res => {
                                    if (res.data?.success) {
                                      const data = res.data.data || [];
                                      setDapps(prev => [...prev, ...data]);
                                      setDappsPagination(res.data.pagination || dappsPagination);
                                    }
                                  }).catch(console.error).finally(() => setLoadingMoreDapps(false));
                                }}
                                disabled={loadingMoreDapps}
                                className="px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50"
                                style={{ backgroundColor: '#f1f5f9', color: '#0f172a', fontFamily: 'Raleway, sans-serif', border: '1px solid #e5e7eb' }}
                              >
                                {loadingMoreDapps ? 'Loading...' : 'Load More'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {bottomRightTab === 'tokens' && (
                    <>
                      {isLoadingTokens ? (
                        <div className="py-6 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>Loading tokens...</div>
                      ) : tokens.length === 0 ? (
                        <div className="py-6 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>No tokens found for this user</div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Chain</th>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Token address</th>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Symbol</th>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>Balance</th>
                                  <th className="text-left py-2 px-3 text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>USD value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tokens.map((token, index) => (
                                  <tr key={index} style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                                    <td className="py-2 px-3 text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{token.chain || 'N/A'}</td>
                                    <td className="py-2 px-3 font-mono text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                                      <div className="flex items-center gap-2">
                                        <span>{token.tokenAddress ? formatWallet(token.tokenAddress) : 'N/A'}</span>
                                        {token.tokenAddress && <CopyButton address={token.tokenAddress} addressKey={`token-br-${index}`} />}
                                      </div>
                                    </td>
                                    <td className="py-2 px-3 text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{token.symbol || 'N/A'}</td>
                                    <td className="py-2 px-3 text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{token.balance ? `${token.balance} ${token.symbol}` : 'N/A'}</td>
                                    <td className="py-2 px-3 text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{token.usdValue ? `$${token.usdValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {tokens.length > 0 && tokensPagination.totalPages > 1 && tokensPagination.page < tokensPagination.totalPages && !loadingMoreTokens && (
                            <div className="mt-4 pt-4 flex justify-center flex-shrink-0" style={{ borderTop: '1px solid #e5e7eb' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  const nextPage = (tokensPagination.page || 1) + 1;
                                  const userIdentifier = user?.distinctId || user?.userId;
                                  if (!userIdentifier || !teamId || !websiteId) return;
                                  setLoadingMoreTokens(true);
                                  axiosInstance.get(`/user-identities/${userIdentifier}/tokens`, {
                                    params: { team_id: teamId, ...websiteScope, lookup_type: 'distinct_id', page: nextPage, limit: 25 }
                                  }).then(res => {
                                    if (res.data?.success) {
                                      const data = res.data.data || [];
                                      setTokens(prev => [...prev, ...data]);
                                      setTokensPagination(res.data.pagination || tokensPagination);
                                    }
                                  }).catch(console.error).finally(() => setLoadingMoreTokens(false));
                                }}
                                disabled={loadingMoreTokens}
                                className="px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50"
                                style={{ backgroundColor: '#f1f5f9', color: '#0f172a', fontFamily: 'Raleway, sans-serif', border: '1px solid #e5e7eb' }}
                              >
                                {loadingMoreTokens ? 'Loading...' : 'Load More'}
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {bottomRightTab === 'allTransactions' && (
                    <>
                      {loadingAllTransactions ? (
                        <div className="py-6 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>Loading all transactions...</div>
                      ) : allTransactionsData.length === 0 ? (
                        <div className="py-6 text-center text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>No transactions for this user</div>
                      ) : (
                        <>
                          {/* All Transactions Timeline - same design as Activity feed, hash · chain · summary in one line */}
                          {(() => {
                            const groupedByDate = {};
                            allTransactionsData.forEach((tx, idx) => {
                              const dateKey = tx.dateTime ? format(new Date(tx.dateTime), 'yyyy-MM-dd') : 'no-date';
                              if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
                              groupedByDate[dateKey].push({ tx, idx });
                            });
                            const dates = Object.keys(groupedByDate).sort((a, b) => (a === 'no-date' ? 1 : b === 'no-date' ? -1 : new Date(b) - new Date(a)));
                            return (
                              <div className="flex-1 min-h-0 overflow-y-auto">
                                {dates.map((dateKey) => {
                                  const entries = groupedByDate[dateKey];
                                  const dateLabel = dateKey === 'no-date' ? 'N/A' : formatDateHeader(entries[0]?.tx?.dateTime || dateKey);
                                  return (
                                    <div key={dateKey} className="mb-4">
                                      <div className="text-sm font-semibold py-2" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif', borderBottom: '1px solid #e5e7eb' }}>
                                        {dateLabel}
                                      </div>
                                      <div>
                                        {entries.map(({ tx, idx }) => {
                                          const displayTime = formatTimeOnly(tx.dateTime);
                                          const hashDisplay = tx.txHash && tx.txHash !== 'N/A' ? formatWallet(tx.txHash) : 'N/A';
                                          return (
                                            <div key={`${tx.txHash || idx}-${idx}`} className="flex">
                                              <div className="py-2 flex-shrink-0" style={{ width: '70px' }}>
                                                <span className="text-xs whitespace-nowrap" style={{ color: '#475569', fontFamily: 'Raleway, sans-serif' }}>
                                                  {displayTime}
                                                </span>
                                              </div>
                                              <div className="flex-1 min-w-0 py-2 flex items-center justify-between gap-2" style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <span className="text-sm font-medium truncate" style={{ color: '#0f172a', fontFamily: 'Raleway, sans-serif' }}>
                                                  {hashDisplay} · {tx.chain || 'N/A'} · {tx.summary || 'N/A'}
                                                </span>
                                                {tx.txHash && tx.txHash !== 'N/A' && <CopyButton address={tx.txHash} addressKey={`alltx-br-${idx}`} />}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                                {allTransactionsData.length > 0 && allTransactionsPagination.totalPages > 1 && allTransactionsPagination.page < allTransactionsPagination.totalPages && (
                                  <div className="mt-4 pt-4 flex justify-center flex-shrink-0" style={{ borderTop: '1px solid #e5e7eb' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const nextPage = (allTransactionsPagination.page || 1) + 1;
                                        const userIdentifier = user?.distinctId || user?.userId;
                                        if (!userIdentifier || !teamId || !websiteId) return;
                                        setLoadingMoreAllTransactions(true);
                                        axiosInstance.get(`/user-identities/${userIdentifier}/timeline`, {
                                          params: { team_id: teamId, ...websiteScope, page: nextPage, limit: 25, lookup_type: 'distinct_id', types_only: 'allTransaction' }
                                        }).then(res => {
                                          if (res.data?.success) {
                                            const data = res.data.data || [];
                                            setAllTransactionsData(prev => [...prev, ...data]);
                                            setAllTransactionsPagination(res.data.pagination || { page: nextPage, limit: 25, total: 0, totalPages: 1 });
                                          }
                                        }).catch(console.error).finally(() => setLoadingMoreAllTransactions(false));
                                      }}
                                      disabled={loadingMoreAllTransactions}
                                      className="px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50"
                                      style={{ backgroundColor: '#f1f5f9', color: '#0f172a', fontFamily: 'Raleway, sans-serif', border: '1px solid #e5e7eb' }}
                                    >
                                      {loadingMoreAllTransactions ? 'Loading...' : 'Load More'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            ) : (
              <div className="py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                No user data available
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Helper component for detail rows - divider inset (not end to end)
const DetailRow = ({ label, value, highlight = false, icon, styles, copyButton, dividerColor, labelColor, valueColor }) => (
  <div className="py-2 px-4">
    <div className="flex items-start justify-between pb-2" style={{ borderBottom: `1px solid ${dividerColor || styles.borderColor}` }}>
      <div className="flex items-center gap-2 text-sm" style={{ color: labelColor || styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
        {icon}
        {label}:
      </div>
      <div 
        className="text-sm font-medium text-right flex items-center justify-end"
        style={{ 
          fontFamily: 'Raleway, sans-serif', 
          color: highlight ? styles.accentColor : (valueColor || styles.textPrimary)
        }}
      >
        {value || 'N/A'}
        {copyButton}
      </div>
    </div>
  </div>
);

// Detail row with dropdown on the value - First/Last only visible when dropdown is open
const DetailRowWithDropdown = ({ label, options, styles, dividerColor, labelColor, valueColor }) => {
  const [selectedIdx, setSelectedIdx] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const opts = options || [];
  const value = opts[selectedIdx]?.value ?? 'N/A';

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (opts.length === 0) return null;
  return (
    <div className="py-2 px-4">
      <div className="flex items-start justify-between pb-2" style={{ borderBottom: `1px solid ${dividerColor || styles.borderColor}` }}>
      <div className="flex items-center gap-2 text-sm" style={{ color: labelColor || styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
        {label}:
      </div>
      <div className="flex-1 min-w-0 flex justify-end" ref={menuRef}>
        {opts.length > 1 ? (
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="flex items-center gap-1 text-sm font-medium min-w-0 max-w-full text-right hover:opacity-80 transition-opacity"
              style={{ color: valueColor || styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}
            >
              <span className="truncate" title={value}>{value || 'N/A'}</span>
              <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: valueColor || styles.textPrimary }} />
            </button>
            {open && (
              <div
                className="absolute right-0 top-full mt-0.5 z-50 py-1 rounded shadow-lg max-w-xs"
                style={{ backgroundColor: styles.primaryBg, border: `1px solid ${dividerColor || styles.borderColor}`, minWidth: '180px' }}
              >
                {opts.map((o, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { setSelectedIdx(i); setOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 transition-colors ${selectedIdx === i ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    style={{ color: valueColor || styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}
                  >
                    <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center mt-0.5">
                      {selectedIdx === i ? <Check className="h-4 w-4" style={{ color: styles.accentColor }} /> : null}
                    </span>
                    <span className="flex-1 min-w-0 break-words">
                      <span className="font-medium">{o.label}:</span> {o.value || 'N/A'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-sm font-medium" style={{ color: valueColor || styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>{value || 'N/A'}</span>
        )}
      </div>
      </div>
    </div>
  );
};

// Other Wallets row: show first wallet, dropdown for rest - divider inset (not end to end)
const OtherWalletsRow = ({ wallets, formatWallet, styles, dividerColor, labelColor, valueColor, CopyButton }) => {
  const [selectedIdx, setSelectedIdx] = React.useState(0);
  const list = Array.isArray(wallets) ? wallets : [];
  if (list.length === 0) {
    return (
      <div className="py-2 px-4">
        <div className="flex items-start justify-between pb-2" style={{ borderBottom: `1px solid ${dividerColor || styles.borderColor}` }}>
          <div className="text-sm" style={{ color: labelColor || styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>Other Wallets:</div>
          <div className="text-sm font-medium" style={{ color: valueColor || styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>N/A</div>
        </div>
      </div>
    );
  }
  const current = list[selectedIdx];
  const displayVal = typeof current === 'string' ? formatWallet(current) : formatWallet(current?.address || current);
  const fullVal = typeof current === 'string' ? current : (current?.address || current);
  return (
    <div className="py-2 px-4">
      <div className="flex items-start justify-between pb-2" style={{ borderBottom: `1px solid ${dividerColor || styles.borderColor}` }}>
      <div className="text-sm" style={{ color: labelColor || styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>Other Wallets:</div>
      <div className="flex items-center gap-2 text-sm font-medium" style={{ color: valueColor || styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
        {list.length > 1 ? (
          <>
            <select
              value={selectedIdx}
              onChange={(e) => setSelectedIdx(Number(e.target.value))}
              className="text-sm border rounded px-2 py-0.5"
              style={{ borderColor: dividerColor || styles.borderColor, color: valueColor || styles.textPrimary, backgroundColor: styles.primaryBg }}
            >
              {list.map((w, i) => (
                <option key={i} value={i}>Wallet {i + 1} of {list.length}</option>
              ))}
            </select>
            <span title={fullVal}>{displayVal}</span>
            {fullVal && <CopyButton address={fullVal} addressKey={`otherWallet-${selectedIdx}`} />}
          </>
        ) : (
          <>
            <span title={fullVal}>{displayVal}</span>
            {fullVal && <CopyButton address={fullVal} addressKey="otherWallet-0" />}
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default UserDetailExpanded;

